import React, { useEffect, useState, useRef } from 'react';
import { categoryService, subCategoryService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { Link } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  Box, Button, Typography, Container, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Alert, CircularProgress, TextField, FormControl, InputLabel, Select, MenuItem, useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

const CategoryManagement = () => {
  const { currentUser } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [categories, setCategories] = useState([]);
  const [sortBy, setSortBy] = useState('id');
  const [sortDir, setSortDir] = useState('asc');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [newCategoryName, setNewCategoryName] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState('');
  const [newSubCategoryName, setNewSubCategoryName] = useState('');
  const subcategoryRef = useRef(null);

  const canManage = currentUser && ['admin', 'moderator', 'superadmin'].includes(currentUser.role);

  const loadCategories = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await categoryService.list();
      setCategories(res.data);
    } catch (e) {
      setError('Errore nel caricamento delle categorie');
    } finally {
      setLoading(false);
    }
  };

  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDir('asc');
    }
  };

  const sortedCategories = [...categories].sort((a, b) => {
    const A = a[sortBy] ?? '';
    const B = b[sortBy] ?? '';
    if (typeof A === 'string' && typeof B === 'string') {
      return sortDir === 'asc' ? A.localeCompare(B) : B.localeCompare(A);
    }
    return sortDir === 'asc' ? (A - B) : (B - A);
  });

  useEffect(() => {
    loadCategories();
  }, []);

  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) return;
    try {
      const res = await categoryService.create({ name: newCategoryName.trim() });
      setCategories([...categories, res.data]);
      setNewCategoryName('');
      setSuccess('Categoria creata');
    } catch (e) {
      setError('Errore nella creazione categoria');
    }
  };

  const handleRenameCategory = async (cat) => {
    const name = prompt('Nuovo nome categoria (chiave)', cat.name);
    if (name === null) return;
    try {
      await categoryService.update(cat.id, { name });
      setCategories(categories.map(c => (c.id === cat.id ? { ...c, name } : c)));
      setSuccess('Categoria aggiornata');
    } catch (e) {
      setError('Errore nell\'aggiornamento categoria');
    }
  };

  const handleDeleteCategory = async (cat) => {
    if (!window.confirm(`Eliminare la categoria "${cat.name}"?`)) return;
    try {
      await categoryService.delete(cat.id);
      setCategories(categories.filter(c => c.id !== cat.id));
      if (selectedCategoryId === String(cat.id)) setSelectedCategoryId('');
      setSuccess('Categoria eliminata');
    } catch (e) {
      setError('Errore nell\'eliminazione categoria');
    }
  };

  const selectedCategory = categories.find(c => String(c.id) === String(selectedCategoryId));

  const handleAddSubCategory = async () => {
    if (!selectedCategory) { setError('Seleziona una categoria'); return; }
    if (!newSubCategoryName.trim()) return;
    try {
      const res = await subCategoryService.create({ name: newSubCategoryName.trim(), category: selectedCategory.id });
      setCategories(categories.map(cat =>
        cat.id === selectedCategory.id
          ? { ...cat, subcategories: [...(cat.subcategories || []), res.data] }
          : cat
      ));
      setNewSubCategoryName('');
      setSuccess('Sottocategoria creata');
    } catch (e) {
      setError('Errore nella creazione sottocategoria');
    }
  };

  const handleRenameSubCategory = async (sub) => {
    const name = prompt('Nuovo nome sottocategoria (chiave)', sub.name);
    if (name === null) return;
    try {
      await subCategoryService.update(sub.id, { name });
      setCategories(categories.map(cat =>
        cat.id === selectedCategory.id
          ? {
            ...cat,
            subcategories: cat.subcategories.map(s => s.id === sub.id ? { ...s, name } : s)
          }
          : cat
      ));
      setSuccess('Sottocategoria aggiornata');
    } catch (e) {
      setError('Errore nell\'aggiornamento sottocategoria');
    }
  };

  const handleDeleteSubCategory = async (sub) => {
    if (!window.confirm(`Eliminare la sottocategoria "${sub.name}"?`)) return;
    try {
      await subCategoryService.delete(sub.id);
      setCategories(categories.map(cat =>
        cat.id === selectedCategory.id
          ? {
            ...cat,
            subcategories: cat.subcategories?.filter(s => s.id !== sub.id)
          }
          : cat
      ));
      setSuccess('Sottocategoria eliminata');
    } catch (e) {
      setError('Errore nell\'eliminazione sottocategoria');
    }
  };

  return (
    <Container component="main" maxWidth={false} disableGutters>
      <Paper elevation={3} sx={{ p: { xs: 2, sm: 3, md: 4 }, mt: { xs: 4, md: 8 }, width: '100%', maxWidth: 1600, mx: 'auto', minWidth: 0, overflow: 'hidden' }}>
        <Box
          sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'center', position: 'relative', mb: 3 }} >
          <Button  component={Link} to="/dashboard" startIcon={<ArrowBackIcon />} variant="outlined" color="primary"
            sx={{ alignSelf: { xs: 'flex-start', sm: 'flex-start' }, mb: { xs: 1, sm: 0 }, textTransform: 'none', }} >
            Dashboard
          </Button>

          <Typography component="h1" variant="h4" gutterBottom
            sx={{ textAlign: { xs: 'left', sm: 'center' }, width: '100%', }} >
            Gestione Categorie
          </Typography>
        </Box>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box>
            {canManage && (
              <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                <TextField
                  label="Nuova categoria"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                />
                <Button variant="contained" onClick={handleAddCategory}>Aggiungi</Button>
              </Box>
            )}

            {!isMobile ? (
              <TableContainer component={Paper} sx={{ mb: 4 }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell onClick={() => toggleSort('id')} style={{ cursor: 'pointer' }}>ID</TableCell>
                      <TableCell onClick={() => toggleSort('name')} style={{ cursor: 'pointer' }}>Nome (chiave)</TableCell>
                      <TableCell align="right">Azioni</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedCategories.map(cat => (
                      <TableRow key={cat.id} hover>
                        <TableCell>{cat.id}</TableCell>
                        <TableCell>{cat.name}</TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                            {canManage && (
                              <>
                                <Button size="small" variant="contained" color="primary" onClick={() => handleRenameCategory(cat)}>Modifica</Button>
                                <Button size="small" variant="contained" color="error" onClick={() => handleDeleteCategory(cat)}>Elimina</Button>
                              </>
                            )}
                            <Button size="small" variant="outlined" onClick={() => {
                              setSelectedCategoryId(String(cat.id));
                              setTimeout(() => subcategoryRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
                            }}>
                              Sottocategorie
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box sx={{ display: 'grid', gap: 2, mb: 4 }}>
                {sortedCategories.map((cat) => (
                  <Paper key={cat.id} variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="caption" color="text.secondary">ID: {cat.id}</Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{cat.name}</Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1.5 }}>
                      {canManage && (
                        <>
                          <Button size="small" variant="contained" color="primary" onClick={() => handleRenameCategory(cat)}>Modifica</Button>
                          <Button size="small" variant="contained" color="error" onClick={() => handleDeleteCategory(cat)}>Elimina</Button>
                        </>
                      )}
                      <Button size="small" variant="outlined" onClick={() => {
                        setSelectedCategoryId(String(cat.id));
                        setTimeout(() => subcategoryRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
                      }}>
                        Sottocategorie
                      </Button>
                    </Box>
                  </Paper>
                ))}
              </Box>
            )}

            <Box ref={subcategoryRef}>
              <Typography variant="h5" gutterBottom sx={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
                Sottocategorie {selectedCategory ? `di "${selectedCategory.name}"` : ''}
              </Typography>
            </Box>

            {selectedCategory && (
              <>
                {canManage && (
                  <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                    <FormControl sx={{ minWidth: 240 }}>
                      <InputLabel id="cat-select-label">Categoria</InputLabel>
                      <Select
                        labelId="cat-select-label"
                        value={selectedCategoryId}
                        label="Categoria"
                        onChange={(e) => setSelectedCategoryId(e.target.value)}
                      >
                        <MenuItem value=""><em>Nessuna</em></MenuItem>
                        {categories.map(c => (
                          <MenuItem value={String(c.id)} key={c.id}>{c.name}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <TextField
                      label="Nuova sottocategoria"
                      value={newSubCategoryName}
                      onChange={(e) => setNewSubCategoryName(e.target.value)}
                    />
                    <Button variant="contained" onClick={handleAddSubCategory} size="medium" sx={{ height: '56px' }} >
                      Aggiungi
                    </Button>
                  </Box>
                )}

                {!isMobile ? (
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>ID</TableCell>
                          <TableCell>Nome (chiave)</TableCell>
                          <TableCell align="right">Azioni</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(selectedCategory.subcategories || [])
                          .slice()
                          .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
                          .map(sub => (
                            <TableRow key={sub.id} hover>
                              <TableCell>{sub.id}</TableCell>
                              <TableCell>{sub.name}</TableCell>
                              <TableCell align="right">
                                {canManage && (
                                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                    <Button size="small" variant="contained" color="primary" onClick={() => handleRenameSubCategory(sub)}>
                                      Modifica
                                    </Button>
                                    <Button size="small" variant="contained" color="error" onClick={() => handleDeleteSubCategory(sub)}>
                                      Elimina
                                    </Button>
                                  </Box>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Box sx={{ display: 'grid', gap: 2 }}>
                    {(selectedCategory.subcategories || [])
                      .slice()
                      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
                      .map((sub) => (
                        <Paper key={sub.id} variant="outlined" sx={{ p: 2 }}>
                          <Typography variant="caption" color="text.secondary">ID: {sub.id}</Typography>
                          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{sub.name}</Typography>
                          {canManage && (
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1.5 }}>
                              <Button size="small" variant="contained" color="primary" onClick={() => handleRenameSubCategory(sub)}>
                                Modifica
                              </Button>
                              <Button size="small" variant="contained" color="error" onClick={() => handleDeleteSubCategory(sub)}>
                                Elimina
                              </Button>
                            </Box>
                          )}
                        </Paper>
                      ))}
                  </Box>
                )}
              </>
            )}
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default CategoryManagement;
